<template>
  <div class="product-traits-wrapper margin-top-s">
    <div v-if="!isMobile || updatePriceComponentSequence">
      <ProductPrice
        :id="productIdValue"
        :product="productObj"
        :class="isSpacingExperimentEnabled || isShopMoreStandardEnabled ? 'margin-bottom-s' : priceMarginFallback"
      />
    </div>
    <a
      v-if="memberProductUrl && !isMobile"
      class="link-body"
      :class="isSpacingExperimentEnabled ? 'margin-bottom-xs' : 'margin-bottom-m'"
      :href="memberProductUrl"
      :target="allowOpeningInNewTab ? '_blank' : '_self'"
    >
      View All Product Details
    </a>
    <div
      v-if="!isMobile"
      class="badges"
    >
      <slot name="pzo" />
      <slot
        v-if="isMcom"
        name="shop-more"
      />
      <slot
        v-if="isMcom"
        name="ppc"
      />
    </div>
    <ColorSwatches
      v-if="showColorSwatches && !isConsolidatedThirdVariantFlag"
      :id="productIdValue"
      :class="isSpacingExperimentEnabled ? 'margin-bottom-xs' : 'margin-bottom-m'"
      :color-props="colorProps"
    />
    <ProductSizes
      :id="productIdValue"
      :product="productObj"
    />
    <a
      v-if="memberProductUrl && isMobile"
      class="link-body margin-bottom-m"
      :href="memberProductUrl"
      :target="allowOpeningInNewTab ? '_blank' : '_self'"
    >
      View All Product Details
    </a>
    <ProductOrientation
      v-if="isMobile && !isSplitThirdVariantFlag && !isConsolidatedThirdVariantFlag"
      :product-id="productIdValue"
    />
    <div v-if="isMobile && !updatePriceComponentSequence">
      <ProductPrice
        :id="productIdValue"
        :product="productObj"
        :class="[
          'margin-bottom-xs',
          {
            'margin-bottom-s': isShopMoreStandardEnabled,
            'margin-bottom-m': isProductPerksEnabled
          }
        ]"
      />
    </div>

    <ProductOrientation
      v-if="!isMobile && !isSplitThirdVariantFlag && !isConsolidatedThirdVariantFlag"
      :product-id="productIdValue"
    />

    <ProtectionPlanBigTicket v-if="isProtectionPlanEligible && !isMemberOfMasterFlag" />

    <QuantitySelector
      v-if="isQuantityEnabled && !isMemberOfMasterFlag"
      v-model="quantity"
      :min="minQuantity"
      :max="maxQuantity"
      size="small"
      :class="isSpacingExperimentEnabled ? 'margin-bottom-xs' : 'margin-bottom-m'"
    />

    <div
      v-if="isWhiteGloveDelivery && !dnfPlacementEnabled"
      class="white-glove-delivery"
      :class="isSpacingExperimentEnabled ? 'margin-bottom-xs' : 'margin-bottom-s'"
    >
      <p class="label margin-bottom-xxs">
        WHITE GLOVE DELIVERY
      </p>
      <div>
        <p class="small">
          Delivery will be scheduled at checkout.
        </p>
      </div>
    </div>

    <div
      v-if="isMobile"
      class="badges"
    >
      <slot name="pzo" />
      <slot
        v-if="isMcom"
        name="shop-more"
      />
      <slot
        v-if="isMcom"
        name="ppc"
      />
    </div>
  </div>
</template>
<script setup>
import { computed, defineAsyncComponent } from 'vue';
import QuantitySelector from '@atomic-ui/quantitySelector';
import ProductOrientation from '@modules/types/components/ProductOrientation';
import { useColor } from '@modules/color/composables';
import { useProductData } from '@composables/useProductData';
import { useQuantity } from '@modules/product';
import { useFeatureEligibility, useProductFlags } from '@modules/featureControl';
import { usePageData } from '@composables/usePageData';
import ColorSwatches from '@components/ColorSwatches';
import ProductSizes from '@components/ProductCard/ProductSize';
import { useBrand } from '@composables/useBrand';
import { useProductType } from '@composables/useProductType';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useExperiments } from '@composables/useExperiments';
import ProtectionPlanBigTicket from '@components/ProtectionPlanBigTicket';
import { useAvailabilityBigTicket } from '@modules/availability/composables/useAvailabilityBigTicket';

const { isMcom } = useBrand();
const { isMobile } = useDeviceType();
const { isLatchKey } = useProductType();

/**
 * * Lazy load ProductPrice component
 * @type {import('./ProductPrice.bcom.vue').default | import('./ProductPrice.mcom.vue').default}
 */
const ProductPrice = defineAsyncComponent(() => import(
  /* webpackMode: "eager" */
  /* webpackPreload: true */
  /* webpackChunkName: "PDP_ProductPrice" */
  `./ProductPrice.${process.env.ENTRY_POINT}`
));

const props = defineProps({
  product: {
    /** @type import('vue').PropType<Product> */
    type: Object,
    required: false,
    default() {
      return null;
    },
  },
  /**
   * TODO: use unified way to get product id
   */
  memberProductUrl: {
    type: String,
    required: false,
    default() {
      return null;
    },
  },
  allowOpeningInNewTab: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const pageData = usePageData();
const {
  hasColorSwatchesFlag, hasColorsFlag, isMemberOfMasterFlag, isSplitThirdVariantFlag, isConsolidatedThirdVariantFlag,
} = useProductFlags();
const {
  isQuantityEnabled, isProtectionPlanEligible, isProductPerksEnabled,
} = useFeatureEligibility();
const {
  experimentFlags: {
    updatePriceComponentSequence,
    isSpacingExperimentEnabled,
    finalPriceDisplayEnabled,
    dnfPlacementEnabled,
    isShopMoreStandardEnabled,
  },
} = useExperiments();

const productIdValue = props.product?.id?.toString() || pageData.productId;
const showColorSwatches = isMcom ? hasColorSwatchesFlag : (hasColorsFlag || isLatchKey);
const { isWhiteGloveDelivery } = useAvailabilityBigTicket();

const productData = useProductData(productIdValue);
const productObj = computed(() => props.product || productData.value);
const { colorProps } = useColor(productIdValue);

const {
  maxQuantity, quantity, minQuantity,
} = useQuantity();

// TODO: update experimentation layout
const priceMarginFallback = computed(() => (finalPriceDisplayEnabled ? 'margin-bottom-xs' : 'margin-bottom-m'));
</script>

<style scoped lang="scss">
.product-traits-wrapper {
  display: flex;
  flex-direction: column;
}

:deep(){
  .select-container {
    margin-bottom: 0;
  }
}
</style>

<style lang="scss">
@import '~settingSCSS';

.product-traits-wrapper {
  .badges {
    > div {
      &:not(:last-child) {
        &:not(:empty) {
          @include margin-bottom-xxs;
        }
      }

      &:last-child {
        &:not(:empty) {
          @include margin-bottom-s;
        }
      }
    }
  }
}
</style>
